import React, { createContext, useContext, useEffect, useState } from "react";

export const CREDIT_COST_KEYS = {
  generate_bullets: "generate_bullets",
  strategic_analysis: "strategic_analysis",
  roast: "roast",
  analyze_listing: "analyze_listing",
  audit: "audit",
  assertions: "assertions",
  translate: "translate",
  regenerate_bullet: "regenerate_bullet",
  chat_message: "chat_message",
} as const;

export type CreditCostKey = keyof typeof CREDIT_COST_KEYS;

interface AppConfig {
  analysisProjectCost: number;
  bulletGenerationCost: number;
  bulletRegenerationCost: number;
  chatWordsPerCredit: number;
  creditCosts: Record<CreditCostKey, number>;
}

interface ConfigContextType {
  config: AppConfig;
  loading: boolean;
  getCreditCost: (key: CreditCostKey) => number;
}

const defaultCreditCosts: Record<CreditCostKey, number> = {
  generate_bullets: 15,
  strategic_analysis: 10,
  roast: 10,
  analyze_listing: 8,
  audit: 5,
  assertions: 5,
  translate: 5,
  regenerate_bullet: 3,
  chat_message: 2,
};

const defaultConfig: AppConfig = {
  analysisProjectCost: 10,
  bulletGenerationCost: 20,
  bulletRegenerationCost: 5,
  chatWordsPerCredit: 50,
  creditCosts: defaultCreditCosts,
};

const ConfigContext = createContext<ConfigContextType>({
  config: defaultConfig,
  loading: true,
  getCreditCost: (key) => defaultCreditCosts[key] ?? 0,
});

export const useConfig = () => useContext(ConfigContext);

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/.netlify/functions/getAppConfig");
        if (response.ok) {
          const data = await response.json();
          const mergedCreditCosts = { ...defaultCreditCosts, ...(data.creditCosts || {}) };
          setConfig({
            ...defaultConfig,
            ...data,
            creditCosts: mergedCreditCosts,
          });
        }
      } catch (error) {
        console.error("Failed to load app config:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const getCreditCost = (key: CreditCostKey) =>
    config.creditCosts?.[key] ?? defaultCreditCosts[key] ?? 0;

  return (
    <ConfigContext.Provider value={{ config, loading, getCreditCost }}>
      {children}
    </ConfigContext.Provider>
  );
};
